import { memo, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatDuration } from 'video-data-lib/utils/time';
import { TEXT_COLOR } from '../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const HOVER_POPOVER_ARROW_WIDTH = 20;
const HOVER_POPOVER_MIN_SPACING_FROM_EDGE = 5;
const SEEK_BAR_HORIZONTAL_PADDING = 10;
const DEFAULT_POPOVER_WIDTH = 220;
const HoverPopoverContainer = styled.div.withConfig({
  displayName: "SeekBarHoverPopover__HoverPopoverContainer"
})(["position:absolute;display:flex;flex-direction:column;gap:5px;align-items:center;bottom:25px;transform:translateX(-50%);min-width:50px;max-width:220px;padding:5px;text-align:center;background-color:white;color:", ";border-radius:3px;box-shadow:0 0 10px 0 rgba(50,50,50,0.5);z-index:5;cursor:initial;user-select:none;pointer-events:none;"], TEXT_COLOR);
const PreviewThumbnail = styled.div.withConfig({
  displayName: "SeekBarHoverPopover__PreviewThumbnail"
})(["width:", "px;height:", "px;max-width:100%;zoom:0.75;"], ({
  width
}) => width, ({
  height
}) => height);
const HoverPopoverArrow = styled.div.withConfig({
  displayName: "SeekBarHoverPopover__HoverPopoverArrow"
})(["position:absolute;content:'';width:0;height:0;bottom:", "px;transform:translateX(-50%);border-left:", "px solid transparent;border-right:", "px solid transparent;border-top:", "px solid white;z-index:5;cursor:initial;"], HOVER_POPOVER_ARROW_WIDTH * 0.75, HOVER_POPOVER_ARROW_WIDTH / 2, HOVER_POPOVER_ARROW_WIDTH / 2, HOVER_POPOVER_ARROW_WIDTH / 2);
const PopoverText = styled.div.withConfig({
  displayName: "SeekBarHoverPopover__PopoverText"
})(["padding-block:0.25rem;"]);

// minimize jank as chapter text jumps between 1 and 2 lines
const PopoverTextWithChapter = styled(PopoverText).withConfig({
  displayName: "SeekBarHoverPopover__PopoverTextWithChapter"
})(["min-height:2.5rem;"]);
export const clampToRange = (value, min, max) => {
  return Math.max(min, Math.min(value, max));
};
const SeekBarHoverPopover = ({
  chapter,
  focusedChapter,
  duration,
  time,
  thumbnailCues,
  mouseXOffset,
  isVisible
}) => {
  const popoverEl = useRef(null);
  const popoverWidthRef = useRef();
  useLayoutEffect(() => {
    var _popoverEl$current;
    if ((_popoverEl$current = popoverEl.current) !== null && _popoverEl$current !== void 0 && _popoverEl$current.clientWidth) {
      popoverWidthRef.current = popoverEl.current.clientWidth;
    }
  }, [time]);
  let popoverWidth = chapter || thumbnailCues !== null && thumbnailCues !== void 0 && thumbnailCues.length ? DEFAULT_POPOVER_WIDTH : 50;
  if (popoverWidthRef.current) {
    popoverWidth = popoverWidthRef.current;
  }
  const minXOffset = popoverWidth / 2 - HOVER_POPOVER_MIN_SPACING_FROM_EDGE;
  const maxXOffset = window.innerWidth - popoverWidth / 2 - (SEEK_BAR_HORIZONTAL_PADDING + HOVER_POPOVER_MIN_SPACING_FROM_EDGE);
  const left = clampToRange(focusedChapter ? focusedChapter.startTime / duration * window.innerWidth : mouseXOffset - SEEK_BAR_HORIZONTAL_PADDING, minXOffset, maxXOffset);
  const arrowLeft = clampToRange(focusedChapter ? focusedChapter.startTime / duration * window.innerWidth + SEEK_BAR_HORIZONTAL_PADDING - HOVER_POPOVER_ARROW_WIDTH / 2 : mouseXOffset - SEEK_BAR_HORIZONTAL_PADDING, HOVER_POPOVER_ARROW_WIDTH / 2, window.innerWidth - SEEK_BAR_HORIZONTAL_PADDING - HOVER_POPOVER_ARROW_WIDTH);
  const currentChapter = focusedChapter || chapter;
  const timeDisplay = formatDuration(time * 1000);
  const thumbnailCue = (thumbnailCues === null || thumbnailCues === void 0 ? void 0 : thumbnailCues.find(cue => {
    return cue.start <= time && cue.end >= time;
  })) || (thumbnailCues === null || thumbnailCues === void 0 ? void 0 : thumbnailCues.at(-1));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(HoverPopoverContainer, {
      ref: popoverEl
      // not using styled-components to avoid generating a new class every mouse move
      ,
      style: {
        left,
        minWidth: chapter ? DEFAULT_POPOVER_WIDTH : undefined,
        display: isVisible ? 'flex' : 'none'
      },
      children: [thumbnailCue &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(PreviewThumbnail, {
        width: thumbnailCue.css.width,
        height: thumbnailCue.css.height,
        style: {
          // not using styled-components to avoid generating a new class for each thumbnail
          background: thumbnailCue.css.background
        }
      }), currentChapter ?
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsxs(PopoverTextWithChapter, {
        children: [timeDisplay, currentChapter && ` - ${currentChapter.title}`]
      }) :
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(PopoverText, {
        className: "seek-bar-popover-time-display",
        children: timeDisplay
      })]
    }), /*#__PURE__*/_jsx(HoverPopoverArrow, {
      style: {
        left: arrowLeft,
        display: isVisible ? 'block' : 'none'
      }
    })]
  });
};
export default /*#__PURE__*/memo(SeekBarHoverPopover);